import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConversationRow, MessageRow } from '@st/contract';
import { qk } from '../api/queries';
import type { ListOutput } from '../api/types';

/**
 * Događaji sobe — doslovno `RoomEvent` iz `apps/api/src/realtime/conversation.ts`.
 * Soba je KANAL, ne izvor istine: poruka je u trenutku objave već u D1.
 */
export type RoomEvent =
  | {
      type: 'message';
      id: string;
      senderId: string;
      body: string | null;
      attachmentKey: string | null;
      createdAt: string;
    }
  | { type: 'read'; userId: string; upToMessageId: string; at: string }
  | { type: 'typing'; userId: string }
  | { type: 'presence'; online: string[] };

/**
 * Minimalno sučelje koje hook traži od veze. Pravi `WebSocket` ga zadovoljava;
 * testovi i mock ubacuju vlastitu implementaciju bez mreže.
 */
export interface SocketLike {
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'unavailable';

/** Eksponencijalni backoff s gornjom granicom — 1s, 2s, 4s, 8s, 16s, pa 30s. */
export const RECONNECT_BASE_MS = 1000;
export const RECONNECT_MAX_MS = 30_000;
export const MAX_RECONNECT_ATTEMPTS = 8;

export function reconnectDelay(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * 2 ** Math.max(0, attempt - 1), RECONNECT_MAX_MS);
}

/** Signal „tipkam” se šalje najviše jednom u ovom prozoru. */
export const TYPING_THROTTLE_MS = 2000;

/** Koliko dugo se prikazuje „tipka…” nakon zadnjeg signala. */
const TYPING_VISIBLE_MS = 4000;

export interface UseConversationSocketOptions {
  conversationId: string | null;
  /** Bez tokena nema handshakea — hook tada ostaje u `idle`. */
  url: string | null;
  /** Vlastiti id: određuje `mine` na porukama koje stignu socketom. */
  meId: string | null;
  socketFactory?: SocketFactory;
  /** Isključeno u testovima koji mjere samo jedan pokušaj. */
  enabled?: boolean;
}

export interface ConversationSocket {
  status: SocketStatus;
  /** Broj dosadašnjih neuspjelih pokušaja — vidljivo u testovima i UI-ju. */
  attempts: number;
  /** Kad veza ne radi, ekran mora i dalje raditi na povlačenje. */
  shouldPoll: boolean;
  online: string[];
  partnerTyping: boolean;
  sendTyping: () => void;
}

const defaultFactory: SocketFactory = (url) => new WebSocket(url) as unknown as SocketLike;

/**
 * Uživo sloj razgovora.
 *
 * Pristigla poruka se UPISUJE u TanStack Query cache (`setQueryData`), nikad se
 * ne poziva `invalidateQueries` — inače bi svaka tuđa poruka izazvala novi
 * dohvat cijele povijesti. Kad veza padne, `shouldPoll` prebacuje ekran na
 * povlačenje svakih 15 s: degradacija, ne bijeli ekran.
 */
export function useConversationSocket({
  conversationId,
  url,
  meId,
  socketFactory = defaultFactory,
  enabled = true,
}: UseConversationSocketOptions): ConversationSocket {
  const qc = useQueryClient();
  const [status, setStatus] = useState<SocketStatus>('idle');
  const [attempts, setAttempts] = useState(0);
  const [online, setOnline] = useState<string[]>([]);
  const [typingAt, setTypingAt] = useState<number | null>(null);

  const socketRef = useRef<SocketLike | null>(null);
  const lastTypingSent = useRef(0);

  const meIdRef = useRef(meId);
  meIdRef.current = meId;

  const applyEvent = useCallback(
    (event: RoomEvent) => {
      if (!conversationId) return;
      const me = meIdRef.current;

      if (event.type === 'presence') {
        setOnline(event.online);
        return;
      }

      if (event.type === 'typing') {
        if (event.userId !== me) setTypingAt(Date.now());
        return;
      }

      if (event.type === 'read') {
        // Tuđe čitanje mojih poruka — kvačica, bez ponovnog dohvata.
        if (event.userId === me) return;
        qc.setQueryData<ListOutput<MessageRow>>(qk.chatMessages(conversationId), (old) =>
          old
            ? {
                items: old.items.map((m) =>
                  m.mine && m.readAt === null ? { ...m, readAt: event.at } : m,
                ),
              }
            : old,
        );
        return;
      }

      // event.type === 'message'
      const incoming: MessageRow = {
        id: event.id,
        senderId: event.senderId,
        mine: me !== null && event.senderId === me,
        body: event.body,
        // Soba nosi ključ priloga; potpisani URL zna samo REST, pa ga ostavljamo null
        // dok se povijest sljedeći put ne dohvati.
        attachmentUrl: null,
        readAt: null,
        createdAt: event.createdAt,
      };

      qc.setQueryData<ListOutput<MessageRow>>(qk.chatMessages(conversationId), (old) => {
        if (!old) return old;
        // Vlastita poruka je već u cacheu iz optimističnog upisa — ne dupliciraj je.
        if (old.items.some((m) => m.id === incoming.id)) return old;
        if (incoming.mine) return old;
        return { items: [...old.items, incoming] };
      });

      // Popis razgovora nosi vrijeme zadnje poruke; jeftin upis umjesto dohvata.
      qc.setQueryData<ListOutput<ConversationRow>>(qk.conversations, (old) =>
        old
          ? {
              items: old.items.map((c) =>
                c.id === conversationId ? { ...c, lastMessageAt: event.createdAt } : c,
              ),
            }
          : old,
      );
    },
    [conversationId, qc],
  );

  useEffect(() => {
    if (!enabled || !conversationId || !url) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const connect = () => {
      if (cancelled) return;
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

      let socket: SocketLike;
      try {
        socket = socketFactory(url);
      } catch {
        scheduleRetry();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        setAttempts(0);
        setStatus('open');
      };
      socket.onmessage = (event) => {
        if (cancelled) return;
        let parsed: RoomEvent;
        try {
          parsed = JSON.parse(event.data) as RoomEvent;
        } catch {
          return;
        }
        applyEvent(parsed);
      };
      socket.onerror = () => {
        // `onclose` uvijek slijedi — ponovno spajanje se pokreće tamo, na jednom mjestu.
      };
      socket.onclose = () => {
        if (cancelled) return;
        socketRef.current = null;
        scheduleRetry();
      };
    };

    const scheduleRetry = () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        // Dalje ne pokušavamo — ekran ostaje na pollingu umjesto da beskonačno zvoni.
        setStatus('unavailable');
        return;
      }
      setStatus('reconnecting');
      timer = setTimeout(connect, reconnectDelay(attempt));
    };

    connect();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
      }
      setStatus('idle');
      setAttempts(0);
      setOnline([]);
    };
  }, [enabled, conversationId, url, socketFactory, applyEvent]);

  // „tipka…” sam sebe gasi; bez toga bi indikator ostao zauvijek nakon zadnjeg signala.
  useEffect(() => {
    if (typingAt === null) return;
    const timer = setTimeout(() => setTypingAt(null), TYPING_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [typingAt]);

  const sendTyping = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || status !== 'open') return;
    const now = Date.now();
    if (now - lastTypingSent.current < TYPING_THROTTLE_MS) return;
    lastTypingSent.current = now;
    try {
      socket.send(JSON.stringify({ type: 'typing' }));
    } catch {
      // Veza je pukla između provjere i slanja — `onclose` pokreće ponovno spajanje.
    }
  }, [status]);

  return {
    status,
    attempts,
    shouldPoll: status !== 'open',
    online,
    partnerTyping: typingAt !== null,
    sendTyping,
  };
}
